import {InhaberEntitiesSelectors, MenuItem, RouterActions, RouterSelectors, SplitButtonVariant} from '@adnova/jf-ng-components';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {combineLatest, map, Observable, Subject, takeUntil} from 'rxjs';
import {Store} from '@ngrx/store';
import {AppState} from '../../../store/states/app.state';
import {NavigationLink} from './navigation-link.interface';
import {AppNavigationSelectors} from './+store/app-navigation.selectors';
import {NavigationButton, NavigationMenuItem} from './navigation-button.interface';


interface ButtonViewModel {
  source: NavigationButton;
  id: string;
  label: string;
  variant: NavigationButton['variant'];
  icon?: NavigationButton['icon'];
  iconPosition: 'left' | 'right';
  disabled: boolean;
  isSplit: boolean;
  splitVariant: SplitButtonVariant;
  menuItems: MenuItem[];
}


@Component({
  selector: 'app-navigation',
  templateUrl: './app-navigation.component.html',
  styleUrls: ['./app-navigation.component.scss']
})
export class AppNavigationComponent implements OnInit, OnDestroy {

  private readonly _destroyed$ = new Subject<void>();

  private _navigationLinks: NavigationLink[] = [];

  protected inhaberId?: string;

  readonly buttonViewModels$: Observable<ButtonViewModel[]> = combineLatest([
    this.store.select(RouterSelectors.dataByKey<NavigationButton[]>('buttons')),
    this.store.select(AppNavigationSelectors.allButtonStates),
  ]).pipe(
    map(([buttons, buttonStates]) => (buttons ?? []).map(
      button => this.toViewModel(button, buttonStates),
    )),
  );

  constructor(
    private store: Store<AppState>,
  ) {
  }

  get navigationLinks(): NavigationLink[] {
    return this._navigationLinks;
  }

  set navigationLinks(value: NavigationLink[]) {
    this._navigationLinks = value;
  }

  ngOnInit(): void {

    this.store.select(RouterSelectors.dataByKey<NavigationLink[]>('navigationLinks')).pipe(
      takeUntil(this._destroyed$),
    ).subscribe((navigationLinks) => {
      if (navigationLinks) {
        this.navigationLinks = navigationLinks;
      } else {
        this.navigationLinks = [];
      }
    });

    combineLatest([
      this.store.select(InhaberEntitiesSelectors.currentInhaberId),
      this.store.select(InhaberEntitiesSelectors.refInhaberId),
      this.store.select(InhaberEntitiesSelectors.fullInhaberList),
    ]).pipe(
      takeUntil(this._destroyed$),
    ).subscribe(([currentInhaberId, refInhaberId, fullInhaberList]) => {
      if (currentInhaberId) {
        this.inhaberId = currentInhaberId;
      } else if (refInhaberId) {
        this.inhaberId = refInhaberId;
      } else if (fullInhaberList.length > 0) {
        this.inhaberId = fullInhaberList[0].id;
      }
    });
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  doButtonAction(button: NavigationButton | NavigationMenuItem): void {
    if (button.action) {
      const action = button.action as any;
      if (action.url && this.inhaberId) {
        this.store.dispatch(RouterActions.navigateByUrl({
          url: action.url.replace(':inhaber', this.inhaberId),
        }));
      } else {
        this.store.dispatch(button.action);
      }
    }
  }

  trackByButtonId(index: number, viewModel: ButtonViewModel): string {
    return viewModel.id;
  }

  private toViewModel(
    button: NavigationButton,
    buttonStates: { [buttonId: string]: boolean },
  ): ButtonViewModel {
    const isEnabled = (id: string): boolean => buttonStates[id] ?? true;
    const menuItems = button.menuItems ?? [];

    return {
      source: button,
      id: button.id,
      label: button.label,
      variant: button.variant,
      icon: button.icon,
      iconPosition: button.iconPosition ?? 'left',
      disabled: !isEnabled(button.id),
      isSplit: menuItems.length > 0,
      splitVariant: button.variant as SplitButtonVariant,
      menuItems: menuItems.map(menuItem => ({
        id: menuItem.id,
        label: menuItem.label,
        iconName: menuItem.icon,
        disabled: !isEnabled(menuItem.id),
        action: () => this.doButtonAction(menuItem),
      })),
    };
  }

}
